import { getTranslatedField } from 'src/utils/translationUtils';
import LocalizedLink from './LocalizedLink';
import { TALLINN_TIMEZONE } from '@shared/constants';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { DayPicker } from 'react-day-picker';
import { Package, Service, TimeSlot } from '@shared/types';
import { useI18n } from 'src/i18n/i18nContext';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import {
  addMinutes,
  addMonths,
  endOfMonth,
  format,
  isToday,
  parseISO,
  setHours,
  setMinutes,
  startOfMonth,
} from 'date-fns';
import { getAvailabilityDates, getAvailableTimes } from 'src/services/api';
import { useLocation } from 'react-router-dom';
import { BookingCartPackageItem, useBookingCartStore } from 'src/stores/bookingCartStore';
import { clearEcommerce, formatServiceItem, pushToDataLayer } from 'src/utils/dataLayer';

interface LocationState {
  bookingId?: string;
  currentDate?: string;
  currentTime?: string;
  preselectedDate?: Date;
  preselectedTime?: string;
  isChanging?: boolean;
  cartItemId?: string;
  preselectedServices?: { serviceId: string; startTime: string }[];
}

interface ServiceBookingProps {
  pkg: Package;
  service: Service;
  setLastOperationWasChange: React.Dispatch<React.SetStateAction<boolean>>;
  setShowCartNotification: React.Dispatch<React.SetStateAction<boolean>>;
}

const ServiceBooking = (props: ServiceBookingProps) => {
  const { pkg, service, setLastOperationWasChange, setShowCartNotification } = props;
  const serviceId = service?.id;

  const { t, language } = useI18n();
  const location = useLocation();

  const { items, addBooking, updateBooking } = useBookingCartStore();

  const locationState = location.state as LocationState | null;
  const isChanging = locationState?.isChanging || false;
  const cartItemIdToChange = locationState?.cartItemId;
  const preselectedServiceStartTime = locationState?.preselectedServices?.find(
    (s) => s.serviceId === serviceId
  )?.startTime;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    if (preselectedServiceStartTime) return new Date(preselectedServiceStartTime);
    return locationState?.preselectedDate ? new Date(locationState.preselectedDate) : undefined;
  });
  const [month, setMonth] = useState<Date | undefined>(() => {
    if (preselectedServiceStartTime) return new Date(preselectedServiceStartTime);
    return locationState?.preselectedDate ? new Date(locationState.preselectedDate) : new Date();
  });
  const [initialDateResolved, setInitialDateResolved] = useState<boolean>(() => {
    return !!preselectedServiceStartTime || !!locationState?.preselectedDate;
  });
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);

  const [loadingSlots, setLoadingSlots] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const findFirstDateCalledRef = useRef(false);
  const lastFetchedDateRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      serviceId &&
      !preselectedServiceStartTime &&
      !locationState?.preselectedDate &&
      !isChanging
    ) {
      findFirstAvailableDate(serviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, preselectedServiceStartTime]);

  useEffect(() => {
    if (serviceId && month) {
      fetchMonthAvailability(month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, month]);

  useEffect(() => {
    if (serviceId && selectedDate && initialDateResolved) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      if (lastFetchedDateRef.current !== dateStr) {
        lastFetchedDateRef.current = dateStr;
        fetchAvailability(dateStr, selectedDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, selectedDate, initialDateResolved]);

  // This effect runs when the component is already mounted and the location state changes.
  // This is crucial for handling the "Change" action when the user is already on the page for the same service.
  useEffect(() => {
    if (
      locationState?.isChanging &&
      (preselectedServiceStartTime || locationState.preselectedDate)
    ) {
      const newDate = preselectedServiceStartTime
        ? new Date(preselectedServiceStartTime)
        : new Date(locationState.preselectedDate as Date);
      lastFetchedDateRef.current = null;
      setSelectedDate(newDate);
      setMonth(newDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, preselectedServiceStartTime]);

  const findFirstAvailableDate = async (id: string) => {
    if (findFirstDateCalledRef.current) return;
    findFirstDateCalledRef.current = true;

    try {
      const today = new Date();
      const endDate = addMonths(today, 4);
      const startStr = format(today, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');

      const response = await getAvailabilityDates(id, startStr, endStr);
      const availabilityMap = response.data.data;

      const availableDates = Object.entries(availabilityMap)
        .filter(([_, count]) => (count as number) > 0)
        .map(([dateStr]) => dateStr)
        .sort();

      if (availableDates.length > 0) {
        const firstAvailableDate = parseISO(availableDates[0]);
        setSelectedDate(firstAvailableDate);
        setMonth(firstAvailableDate);
        setLoadingSlots(true);
        setInitialDateResolved(true);
      } else {
        setSelectedDate(new Date());
        setInitialFetchDone(true);
        setInitialDateResolved(true);
      }
    } catch (err) {
      console.error('Failed to auto-select first available date', err);
    }
  };

  const fetchMonthAvailability = async (targetMonth: Date) => {
    if (!serviceId) return;

    try {
      const start = format(startOfMonth(targetMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(targetMonth), 'yyyy-MM-dd');

      const response = await getAvailabilityDates(serviceId, start, end);
      const availabilityMap = response.data.data;

      const disabledDates: Date[] = [];
      Object.entries(availabilityMap).forEach(([dateStr, count]) => {
        if (count === 0) {
          disabledDates.push(parseISO(dateStr));
        }
      });

      setUnavailableDates(disabledDates);
    } catch (err) {
      console.error('Failed to fetch month availability', err);
    }
  };

  const fetchAvailability = async (date: string, forDate: Date) => {
    if (!serviceId) return;

    setLoadingSlots(true);
    try {
      const response = await getAvailableTimes(serviceId, date, date);

      const slotsData = response.data.data || [];
      const slots = slotsData.map((slot: any, index: number) => ({
        id: slot.id || `${date}-${slot.time || slot}-${index}`,
        time: slot.time || slot,
        available: slot.available !== false,
        resourceId: slot.resourceId,
      }));

      if (isToday(forDate)) {
        const now = new Date();
        const availableSlots = slots.filter((slot: TimeSlot) => {
          const [hour, minute] = slot.time.split(':').map(Number);
          const slotTime = setMinutes(setHours(forDate, hour), minute);
          return now < slotTime;
        });
        setTimeSlots(availableSlots);
      } else {
        setTimeSlots(slots);
      }

      const preselectedTimeForService = preselectedServiceStartTime
        ? formatInTimeZone(new Date(preselectedServiceStartTime), TALLINN_TIMEZONE, 'HH:mm')
        : locationState?.preselectedTime;

      if (isChanging && preselectedTimeForService) {
        const matchingSlot = slots.find(
          (slot: TimeSlot) => slot.time === preselectedTimeForService
        );
        if (matchingSlot) {
          setSelectedSlot(matchingSlot.id);
          // We no longer clear the state here, allowing the component to remember it's "changing".
        }
      }
    } catch (err) {
      console.error(t.console.errors.fetchingAvailability, err);
      // Fallback to empty slots on error
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
      setInitialFetchDone(true);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      lastFetchedDateRef.current = null;
      setSelectedDate(date);
      setMonth(date);
    }
    setSelectedSlot(null);
  };

  const isSelectedSlotAlreadyBooked = () => {
    if (!selectedSlot || !selectedDate || !service) return false;

    const slot = timeSlots.find((s) => s.id === selectedSlot);
    if (!slot) return false;

    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');

    return items.some((item) => {
      if (item.type === 'service') {
        const itemDateString = formatInTimeZone(
          new Date(item.startTime),
          TALLINN_TIMEZONE,
          'yyyy-MM-dd'
        );
        const itemTimeString = formatInTimeZone(
          new Date(item.startTime),
          TALLINN_TIMEZONE,
          'HH:mm'
        );

        return (
          item.id === service.id &&
          itemDateString === selectedDateString &&
          itemTimeString === slot.time &&
          item.cartItemId !== cartItemIdToChange
        );
      }

      return item.services.some((pkgService) => {
        const itemDateString = formatInTimeZone(
          new Date(pkgService.startTime),
          TALLINN_TIMEZONE,
          'yyyy-MM-dd'
        );
        const itemTimeString = formatInTimeZone(
          new Date(pkgService.startTime),
          TALLINN_TIMEZONE,
          'HH:mm'
        );

        return (
          pkgService.serviceId === service.id &&
          itemDateString === selectedDateString &&
          itemTimeString === slot.time &&
          item.cartItemId !== cartItemIdToChange
        );
      });
    });
  };

  const handleAddToCart = () => {
    if (selectedSlot && selectedDate && service) {
      const slot = timeSlots.find((s) => s.id === selectedSlot);
      if (slot) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const timeStr = slot.time;

        const dateTimeStr = `${dateStr} ${timeStr}`;

        const startDateTime = fromZonedTime(dateTimeStr, TALLINN_TIMEZONE);
        const endDateTime = addMinutes(startDateTime, service.duration);

        const bookingDetails: Omit<BookingCartPackageItem, 'cartItemId' | 'quantity'> = {
          type: 'package',
          id: pkg.id,
          name: getTranslatedField(pkg, 'name', language),
          nameEn: pkg.name,
          price: pkg.price || 0,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          duration: pkg.duration_minutes,
          services: [
            {
              serviceId: service.id,
              serviceName: getTranslatedField(service, 'name', language),
              serviceNameEn: service.name,
              startTime: startDateTime.toISOString(),
              endTime: endDateTime.toISOString(),
              duration: service.duration,
            },
          ],
        };

        const existingPackageItem = items.find(
          (item) => item.type === 'package' && item.id === pkg.id
        );

        if (isChanging && cartItemIdToChange) {
          updateBooking(cartItemIdToChange, bookingDetails);
          setLastOperationWasChange(true);
        } else if (existingPackageItem && existingPackageItem.type === 'package') {
          updateBooking(existingPackageItem.cartItemId, bookingDetails);
          setLastOperationWasChange(true);
        } else {
          addBooking(bookingDetails);
          setLastOperationWasChange(false);
        }

        clearEcommerce();
        // Standardized format: YYYY-MM-DD HH:mm for GA4 data consistency
        const variant = `${dateStr} ${timeStr}`;
        pushToDataLayer('add_to_cart', {
          content: { category: 'booking_service' },
          ecommerce: {
            items: [formatServiceItem(service, variant)],
          },
        });

        setShowCartNotification(true);
        setSelectedSlot(null);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-7">
      <div className="lg:col-span-3 lg:row-span-3">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-md">
          {service.image && (
            <img
              src={service.image}
              alt={getTranslatedField(service, 'name', language)}
              className="mb-4 h-48 w-full rounded-md object-cover"
            />
          )}
          <div className="mb-4 flex items-start justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {getTranslatedField(service, 'name', language)}
            </h2>
            <LocalizedLink
              to="/pricing"
              className="ml-3 whitespace-nowrap rounded-md bg-sky-50 px-3 py-1 text-sm font-medium text-sky-600 transition-colors hover:bg-sky-100"
            >
              {t.nav.pricing}
            </LocalizedLink>
          </div>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {service.duration} {t.booking.minutes}
            </p>
          </div>
          {getTranslatedField(service, 'description', language) && (
            <div className="border-t border-gray-200 pt-4">
              <div
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: getTranslatedField(service, 'description', language) || '',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Date Selection */}
      <div className="lg:col-span-2">
        <h3 className="mb-4 text-lg font-semibold">{t.booking.selectDate}</h3>
        <div className="flex justify-center overflow-x-auto rounded-md bg-white p-4 shadow md:block">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            month={month}
            onMonthChange={setMonth}
            disabled={[{ before: new Date() }, ...unavailableDates]}
            locale={
              language === 'ru'
                ? ruLocale
                : language === 'et'
                  ? etLocale
                  : language === 'fi'
                    ? fiLocale
                    : undefined
            }
            className="mx-auto"
            styles={{
              caption: { color: '#1d4ed8' },
              head: { color: '#1d4ed8' },
            }}
          />
        </div>
      </div>

      {/* Time Slots */}
      <div className="lg:col-span-2">
        <h3 className="mb-4 text-lg font-semibold">{t.booking.selectTime}</h3>
        {loadingSlots || !initialDateResolved || !initialFetchDone ? (
          <div className="py-8" />
        ) : timeSlots.length === 0 ? (
          <div className="py-8 text-center text-gray-500">{t.booking.noAvailableTimesForDate}</div>
        ) : (
          <div className="grid max-h-96 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-4">
            {timeSlots.map((slot) => {
              const isAlreadyBooked =
                selectedDate &&
                items.some((item) => {
                  if (item.type !== 'package') return false;

                  return item.services.some((pkgService) => {
                    const itemDate = formatInTimeZone(
                      new Date(pkgService.startTime),
                      TALLINN_TIMEZONE,
                      'yyyy-MM-dd'
                    );
                    const itemTime = formatInTimeZone(
                      new Date(pkgService.startTime),
                      TALLINN_TIMEZONE,
                      'HH:mm'
                    );
                    const currentDate = formatInTimeZone(
                      selectedDate,
                      TALLINN_TIMEZONE,
                      'yyyy-MM-dd'
                    );
                    return (
                      pkgService.serviceId === serviceId &&
                      itemDate === currentDate &&
                      itemTime === slot.time
                    );
                  });
                });

              return (
                <button
                  key={slot.id}
                  disabled={!slot.available}
                  onClick={() => setSelectedSlot(slot.id)}
                  className={`touch-manipulation rounded-md px-2 py-2 text-xs font-medium transition-colors sm:text-sm ${
                    !slot.available
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : isAlreadyBooked
                        ? 'border-2 border-purple-500 bg-purple-100 text-purple-700'
                        : selectedSlot === slot.id
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {slot.time}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Action Buttons - Below date/time selection, spanning 4 columns */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-4 lg:col-start-4">
        {
          <button
            onClick={handleAddToCart}
            disabled={!selectedSlot || isSelectedSlotAlreadyBooked()}
            className={`w-full rounded-md px-6 py-2 font-medium sm:w-auto ${
              selectedSlot && !isSelectedSlotAlreadyBooked()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            {isChanging ? t.cart.confirmChange : t.cart.addToCart}
          </button>
        }
      </div>
    </div>
  );
};

export default ServiceBooking;
